export interface GameStats {
  totalSolved: number;
  currentStreak: number;
  longestStreak: number;
  lastSolvedDate: string | null;
  solvedDates: string[];
  parScores: number[];
  totalScore: number;
  streakScore: number;
}

export interface SingleProposalStats {
  totalSolved: number;
  totalAttempts: number;
  currentStreak: number;
  longestStreak: number;
  lastSolvedDate: string | null;
  solvedDates: string[];
  failedDates: string[];
}

export interface ParScoreDistribution {
  [key: number]: number;
}

class StatsService {
  private readonly STORAGE_KEY = 'dimension23_stats';
  private readonly SINGLE_PROPOSAL_STORAGE_KEY = 'dimension23_single_proposal_stats';

  private loadStats(): GameStats {
    const stored = localStorage.getItem(this.STORAGE_KEY);
    if (stored) {
      try {
        return JSON.parse(stored);
      } catch (e) {
        console.error('Error parsing stats:', e);
      }
    }
    
    return {
      totalSolved: 0,
      currentStreak: 0,
      longestStreak: 0,
      lastSolvedDate: null,
      solvedDates: [],
      parScores: [],
      totalScore: 0,
      streakScore: 0
    };
  }

  private saveStats(stats: GameStats): void {
    localStorage.setItem(this.STORAGE_KEY, JSON.stringify(stats));
  }

  private getTodayString(): string {
    return new Date().toISOString().split('T')[0];
  }

  private isConsecutiveDay(lastDate: string, currentDate: string): boolean {
    const last = new Date(lastDate);
    const current = new Date(currentDate);
    const diffTime = current.getTime() - last.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays === 1;
  }

  recordCaseSolved(parScore: number, caseDate?: string): void {
    const stats = this.loadStats();
    const solvedDate = caseDate || this.getTodayString();
    
    console.log('Stats recordCaseSolved debug:', {
      caseDate,
      solvedDate,
      existingSolvedDates: [...stats.solvedDates], // Create a copy to avoid reference issues
      alreadySolved: stats.solvedDates.includes(solvedDate)
    });
    
    // Check if already solved for this date
    if (stats.solvedDates.includes(solvedDate)) {
      console.log('Case already solved for date:', solvedDate);
      return;
    }

    console.log('Adding solved date:', solvedDate);
    // Update solved dates
    stats.solvedDates.push(solvedDate);
    stats.totalSolved += 1;
    stats.parScores.push(parScore);
    stats.totalScore += parScore;

    // Update streak (only for today's cases, not archive cases)
    if (!caseDate) {
      if (stats.lastSolvedDate && this.isConsecutiveDay(stats.lastSolvedDate, solvedDate)) {
        stats.currentStreak += 1;
      } else {
        stats.currentStreak = 1;
      }

      // Update longest streak
      if (stats.currentStreak > stats.longestStreak) {
        stats.longestStreak = stats.currentStreak;
      }

      stats.lastSolvedDate = solvedDate;
    }
    stats.streakScore = this.calculateStreakScore(stats);
    
    this.saveStats(stats);
  }

  private calculateStreakScore(stats: GameStats): number {
    // Calculate streak score based on recent performance
    const recentScores = stats.parScores.slice(-stats.currentStreak);
    return recentScores.reduce((sum, score) => sum + score, 0);
  }

  getStats(): GameStats {
    const stats = this.loadStats();
    // Recalculate streak score
    stats.streakScore = this.calculateStreakScore(stats);
    return stats;
  }

  getParScoreDistribution(): ParScoreDistribution {
    const stats = this.loadStats();
    const distribution: ParScoreDistribution = {};
    
    // Initialize all possible scores from -4 to +4
    for (let i = -4; i <= 4; i++) {
      distribution[i] = 0;
    }
    
    // Count occurrences
    stats.parScores.forEach(score => {
      if (distribution[score] !== undefined) {
        distribution[score] += 1;
      }
    });
    
    return distribution;
  }

  getStreakMessage(): string {
    const stats = this.loadStats();
    const today = this.getTodayString();
    
    if (stats.totalSolved === 0) {
      return "Solve cases to record stats";
    } else if (stats.lastSolvedDate === today) {
      if (stats.currentStreak > 1) {
        return `You are on a ${stats.currentStreak} day streak`;
      } else {
        return `You are on a ${stats.currentStreak} day streak`;
      }
    } else if (stats.lastSolvedDate && this.isConsecutiveDay(stats.lastSolvedDate, today)) {
      return `Continue your ${stats.currentStreak} day streak!`;
    } else {
      return `Add to your ${stats.totalSolved} solved cases`;
    }
  }

  resetStats(): void {
    localStorage.removeItem(this.STORAGE_KEY);
  }

  // Single Proposal Mode Stats
  private loadSingleProposalStats(): SingleProposalStats {
    const stored = localStorage.getItem(this.SINGLE_PROPOSAL_STORAGE_KEY);
    if (stored) {
      try {
        return JSON.parse(stored);
      } catch (e) {
        console.error('Error parsing single proposal stats:', e);
      }
    }
    
    return {
      totalSolved: 0,
      totalAttempts: 0,
      currentStreak: 0,
      longestStreak: 0,
      lastSolvedDate: null,
      solvedDates: [],
      failedDates: []
    };
  }

  private saveSingleProposalStats(stats: SingleProposalStats): void {
    localStorage.setItem(this.SINGLE_PROPOSAL_STORAGE_KEY, JSON.stringify(stats));
  }

  recordSingleProposalResult(solved: boolean, caseDate?: string): void {
    const stats = this.loadSingleProposalStats();
    const attemptDate = caseDate || this.getTodayString();
    
    // Check if already attempted for this date
    if (stats.solvedDates.includes(attemptDate) || stats.failedDates.includes(attemptDate)) {
      return;
    }

    // Record the attempt
    stats.totalAttempts += 1;

    if (solved) {
      stats.solvedDates.push(attemptDate);
      stats.totalSolved += 1;

      // Update streak (only for today's cases, not archive cases)
      if (!caseDate) {
        if (stats.lastSolvedDate && this.isConsecutiveDay(stats.lastSolvedDate, attemptDate)) {
          stats.currentStreak += 1;
        } else {
          stats.currentStreak = 1;
        }

        // Update longest streak
        if (stats.currentStreak > stats.longestStreak) {
          stats.longestStreak = stats.currentStreak;
        }

        stats.lastSolvedDate = attemptDate;
      }
    } else {
      stats.failedDates.push(attemptDate);
      
      // Reset streak on failure (only for today's cases)
      if (!caseDate) {
        stats.currentStreak = 0;
      }
    }
    
    this.saveSingleProposalStats(stats);
  }

  getSingleProposalStats(): SingleProposalStats {
    return this.loadSingleProposalStats();
  }

  getSingleProposalSuccessRate(): number {
    const stats = this.loadSingleProposalStats();
    if (stats.totalAttempts === 0) return 0;
    return Math.round((stats.totalSolved / stats.totalAttempts) * 100);
  }

  getSingleProposalStreakMessage(): string {
    const stats = this.loadSingleProposalStats();
    const today = this.getTodayString();
    
    if (stats.totalAttempts === 0) {
      return "Solve cases to record stats";
    } else if (stats.lastSolvedDate === today) {
      if (stats.currentStreak > 1) {
        return `You are on a ${stats.currentStreak} day streak`;
      } else {
        return `You are on a ${stats.currentStreak} day streak`;
      }
    } else if (stats.lastSolvedDate && this.isConsecutiveDay(stats.lastSolvedDate, today)) {
      return `Continue your ${stats.currentStreak} day streak!`;
    } else {
      return `Add to your ${stats.totalSolved} solved cases`;
    }
  }

  resetSingleProposalStats(): void {
    localStorage.removeItem(this.SINGLE_PROPOSAL_STORAGE_KEY);
  }
}

export const statsService = new StatsService();
